import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Paper, Alert, CircularProgress, Stack, Button, Chip, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, Select, MenuItem, Breadcrumbs, Link as MuiLink,
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon, Tune as TuneIcon, Science as PipelineIcon,
} from '@mui/icons-material';
import { analysisFindingsAPI } from '../../services/api';
import { isSimpleRoleKind, SubChoice, DirectRolePickers } from './columnMapping';

/**
 * Pipeline detail + management (#40).
 *
 * Shows the live definition (files + annotations), version history, and the
 * runs that used this pipeline. Cosmetic edits (display_name/description)
 * apply in place; editing the column→role annotations is a contract change
 * that requires a new version and writes a new snapshot. Delete is blocked
 * when runs reference the pipeline.
 */

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return iso; }
}

const PipelineDetail = () => {
  const { name } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [editCosmetic, setEditCosmetic] = useState(false);
  const [editAnnotations, setEditAnnotations] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    analysisFindingsAPI.getRegisteredPipeline(name)
      .then((res) => { setData(res.data); setError(null); setNotFound(false); })
      .catch((err) => {
        if (err.response?.status === 404) setNotFound(true);
        else setError(err.response?.data?.msg || err.message || 'Failed to load pipeline');
      })
      .finally(() => setLoading(false));
  }, [name]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ p: 3, maxWidth: 1300 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink component={RouterLink} to="/analysis/pipelines" underline="hover">Pipelines</MuiLink>
        <Typography color="text.primary">{data?.pipeline?.display_name || name}</Typography>
      </Breadcrumbs>

      {loading && (
        <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={20} /><Typography variant="body2">Loading…</Typography></Stack>
      )}
      {!loading && notFound && (
        <Alert severity="warning">Pipeline “{name}” doesn’t exist. <MuiLink component={RouterLink} to="/analysis/pipelines">Back to pipelines</MuiLink></Alert>
      )}
      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !notFound && !error && data && (
        <>
          <Header data={data}
            onEditCosmetic={() => setEditCosmetic(true)}
            onEditAnnotations={() => setEditAnnotations(true)}
            onDelete={() => setConfirmDelete(true)} />
          <FilesSection files={data.files} />
          <ColumnMappingSection files={data.files} annotations={data.annotations} />
          <VersionsSection versions={data.versions} />
          <RunsSection runs={data.runs} />

          {editCosmetic && (
            <CosmeticEditDialog pipeline={data.pipeline}
              onClose={() => setEditCosmetic(false)}
              onSaved={() => { setEditCosmetic(false); load(); }} />
          )}
          {editAnnotations && (
            <AnnotationsEditDialog data={data}
              onClose={() => setEditAnnotations(false)}
              onSaved={() => { setEditAnnotations(false); load(); }} />
          )}
          {confirmDelete && (
            <DeleteDialog pipeline={data.pipeline} runs={data.runs}
              onClose={() => setConfirmDelete(false)}
              onDeleted={() => navigate('/analysis/pipelines')} />
          )}
        </>
      )}
    </Box>
  );
};

function Header({ data, onEditCosmetic, onEditAnnotations, onDelete }) {
  const { pipeline, runs } = data;
  const referenced = runs.length > 0;
  return (
    <Paper sx={{ p: 3, mb: 3 }} elevation={1}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PipelineIcon color="primary" />
            <Typography variant="h5">{pipeline.display_name}</Typography>
            <Chip label={`v${pipeline.version}`} size="small" />
            <Chip label={pipeline.kind} size="small" variant="outlined" />
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {pipeline.name} · module: {pipeline.module_name || '—'}
          </Typography>
          {pipeline.description && <Typography variant="body2" sx={{ mt: 1 }}>{pipeline.description}</Typography>}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button size="small" startIcon={<EditIcon />} onClick={onEditCosmetic}>Edit details</Button>
          <Button size="small" startIcon={<TuneIcon />} onClick={onEditAnnotations}>Edit mapping</Button>
          <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={onDelete} disabled={referenced}>
            Delete
          </Button>
        </Stack>
      </Stack>
      {referenced && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Cannot delete — {runs.length} run{runs.length === 1 ? '' : 's'} reference this pipeline.
        </Typography>
      )}
    </Paper>
  );
}

function FilesSection({ files }) {
  return (
    <Paper sx={{ p: 3, mb: 3 }} elevation={1}>
      <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Input files</Typography>
      {files.map((f) => (
        <Box key={f.file_key} sx={{ mb: 1 }}>
          <Typography variant="body2">
            <strong>{f.file_key}</strong>{f.required ? '' : ' (optional)'} — {(f.expected_columns || []).length} columns,
            specimen identifier in <code>{f.database_id_column}</code>
          </Typography>
        </Box>
      ))}
    </Paper>
  );
}

// Lists every expected column (in file order), so the user can check it
// against their actual file header. The specimen-ID column and any unmapped
// columns are shown alongside the annotated ones.
function ColumnMappingSection({ files, annotations }) {
  const annoByKey = new Map();
  for (const a of annotations) annoByKey.set(`${a.file_key}|${a.column_name}`, a);

  const rows = [];
  for (const f of files) {
    for (const col of f.expected_columns || []) {
      rows.push({
        file_key: f.file_key,
        column: col,
        isId: col === f.database_id_column,
        anno: annoByKey.get(`${f.file_key}|${col}`),
      });
    }
  }
  const multiFile = files.length > 1;

  return (
    <Paper sx={{ p: 0, mb: 3 }} elevation={1}>
      <Box sx={{ p: 3, pb: 1 }}>
        <Typography variant="subtitle1">Column mapping</Typography>
        <Typography variant="caption" color="text.secondary">
          Every column expected in the upload file and how each is interpreted — check this against your file’s header row.
        </Typography>
      </Box>
      <Divider />
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {multiFile && <TableCell>File</TableCell>}
              <TableCell>Column</TableCell><TableCell>Role</TableCell>
              <TableCell>Rank</TableCell><TableCell>Kind</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={multiFile ? 5 : 4}><Typography variant="body2" color="text.secondary">No expected columns declared.</Typography></TableCell></TableRow>
            )}
            {rows.map((r, i) => (
              <TableRow key={i}>
                {multiFile && <TableCell>{r.file_key}</TableCell>}
                <TableCell sx={{ fontFamily: 'monospace' }}>{r.column}</TableCell>
                <TableCell>
                  {r.isId
                    ? <Chip label="Specimen ID" size="small" />
                    : r.anno
                      ? r.anno.role
                      : <Typography variant="body2" color="text.disabled">unmapped</Typography>}
                </TableCell>
                <TableCell>{r.anno?.taxonomy_rank || '—'}</TableCell>
                <TableCell>{r.anno?.kind || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function VersionsSection({ versions }) {
  return (
    <Paper sx={{ p: 0, mb: 3 }} elevation={1}>
      <Box sx={{ p: 3, pb: 1 }}><Typography variant="subtitle1">Version history</Typography></Box>
      <Divider />
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Version</TableCell><TableCell>Change</TableCell>
              <TableCell>Date</TableCell><TableCell>Current</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {versions.map((v) => (
              <TableRow key={v.id} hover>
                <TableCell>v{v.version}</TableCell>
                <TableCell>{v.change_summary || '—'}</TableCell>
                <TableCell>{formatDate(v.created_at)}</TableCell>
                <TableCell>{v.is_current ? <Chip label="current" size="small" color="primary" /> : ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function RunsSection({ runs }) {
  return (
    <Paper sx={{ p: 0, mb: 3 }} elevation={1}>
      <Box sx={{ p: 3, pb: 1 }}><Typography variant="subtitle1">Runs using this pipeline ({runs.length})</Typography></Box>
      <Divider />
      {runs.length === 0 ? (
        <Box sx={{ p: 3, pt: 1 }}><Typography variant="body2" color="text.secondary">No runs yet.</Typography></Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Run</TableCell><TableCell>Version used</TableCell><TableCell>Date</TableCell><TableCell align="right">Findings</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {runs.map((r) => (
                <TableRow key={r.run_id} hover>
                  <TableCell>
                    <MuiLink component={RouterLink} to={`/analysis/runs/${r.run_number}`} underline="hover">Run #{r.run_number}</MuiLink>
                  </TableCell>
                  <TableCell>v{r.producer_version}</TableCell>
                  <TableCell>{formatDate(r.imported_at)}</TableCell>
                  <TableCell align="right">{Number(r.finding_count).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

// --- edit dialogs ------------------------------------------------------------

function CosmeticEditDialog({ pipeline, onClose, onSaved }) {
  const [displayName, setDisplayName] = useState(pipeline.display_name || '');
  const [description, setDescription] = useState(pipeline.description || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const save = () => {
    setSaving(true); setErr(null);
    analysisFindingsAPI.editPipeline(pipeline.name, { display_name: displayName, description })
      .then(onSaved)
      .catch((e) => { setErr(e.response?.data?.msg || e.message || 'Save failed'); setSaving(false); });
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit pipeline</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {err && <Alert severity="error">{err}</Alert>}
          <TextField label="Display name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} fullWidth />
          <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline minRows={2} />
          <Typography variant="caption" color="text.secondary">
            These are cosmetic edits and don’t create a new version. To change the column mapping, use “Edit mapping”.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={save} variant="contained" disabled={saving || !displayName.trim()}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AnnotationsEditDialog({ data, onClose, onSaved }) {
  const { pipeline, files, annotations } = data;
  const file = files[0] || { expected_columns: [], database_id_column: '' };
  const columns = file.expected_columns || [];
  const idColumn = file.database_id_column;

  const [roles, setRoles] = useState(null);
  const [catalogErr, setCatalogErr] = useState(null);
  // assignments: { [column]: { role, taxonomy_rank?, kind? } }
  const [assignments, setAssignments] = useState(() => {
    const a = {};
    for (const an of annotations) {
      a[an.column_name] = { role: an.role, taxonomy_rank: an.taxonomy_rank || undefined, kind: an.kind || undefined };
    }
    return a;
  });
  const [version, setVersion] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    analysisFindingsAPI.getResultTypes()
      .then((res) => setRoles(res.data.result_types?.[pipeline.kind]?.registration_roles || []))
      .catch((e) => setCatalogErr(e.message || 'Failed to load role catalog'));
  }, [pipeline.kind]);

  const save = () => {
    const annos = Object.entries(assignments)
      .filter(([col, a]) => a.role && col !== idColumn)
      .map(([col, a]) => ({ file_key: 'main', column: col, role: a.role, taxonomy_rank: a.taxonomy_rank, kind: a.kind }));
    setSaving(true); setErr(null);
    analysisFindingsAPI.editPipeline(pipeline.name, { version, annotations: annos })
      .then(onSaved)
      .catch((e) => {
        const body = e.response?.data;
        const msg = body?.errors?.length ? `${body.msg}: ${body.errors.join('; ')}` : (body?.msg || e.message || 'Save failed');
        setErr(msg); setSaving(false);
      });
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit column mapping</DialogTitle>
      <DialogContent>
        {catalogErr && <Alert severity="error" sx={{ mt: 1 }}>{catalogErr}</Alert>}
        {!roles && !catalogErr && <CircularProgress size={20} sx={{ mt: 2 }} />}
        {roles && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {err && <Alert severity="error">{err}</Alert>}
            <Alert severity="info">
              Changing the column mapping creates a new version of this pipeline, so any runs
              already ingested with it stay tied to the mapping they actually used (you can always
              look back at what a run ran against). Enter a new version number below —
              current is v{pipeline.version}.
            </Alert>
            <TextField label="New version" value={version} onChange={(e) => setVersion(e.target.value)}
              placeholder={`e.g. ${pipeline.version} → 1.1.0`} sx={{ maxWidth: 240 }} />
            {isSimpleRoleKind(roles) ? (
              <DirectRolePickers
                columns={columns}
                roles={roles}
                assignments={assignments}
                setAssignments={setAssignments}
                idColumn={idColumn}
                onChangeIdColumn={null}
              />
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow><TableCell>Column</TableCell><TableCell>Role</TableCell><TableCell>Sub-choice</TableCell></TableRow>
                  </TableHead>
                  <TableBody>
                    {columns.map((col) => {
                      const isId = col === idColumn;
                      const a = assignments[col] || { role: null };
                      return (
                        <TableRow key={col}>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{col}</TableCell>
                          <TableCell>
                            {isId ? <Chip label="Specimen ID" size="small" /> : (
                              <FormControl size="small" sx={{ minWidth: 170 }}>
                                <Select value={a.role || ''} displayEmpty
                                  onChange={(e) => setAssignments((s) => ({ ...s, [col]: { ...s[col], role: e.target.value || null } }))}>
                                  <MenuItem value=""><em>Unmapped</em></MenuItem>
                                  {roles.map((r) => <MenuItem key={r.key} value={r.key}>{r.label}</MenuItem>)}
                                </Select>
                              </FormControl>
                            )}
                          </TableCell>
                          <TableCell>
                            {!isId && a.role && (
                              <SubChoice role={roles.find((r) => r.key === a.role)} assignment={a}
                                onChange={(patch) => setAssignments((s) => ({ ...s, [col]: { ...s[col], ...patch } }))} />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={save} variant="contained" disabled={saving || !version.trim() || !roles}>
          {saving ? 'Saving…' : 'Save new version'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DeleteDialog({ pipeline, runs, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState(null);
  const blocked = runs.length > 0;

  const doDelete = () => {
    setDeleting(true); setErr(null);
    analysisFindingsAPI.deletePipeline(pipeline.name)
      .then(onDeleted)
      .catch((e) => {
        if (e.response?.status === 409) {
          setErr('This pipeline is referenced by analysis runs and cannot be deleted.');
        } else {
          setErr(e.response?.data?.msg || e.message || 'Delete failed');
        }
        setDeleting(false);
      });
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Delete pipeline?</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {err && <Alert severity="error">{err}</Alert>}
          {blocked ? (
            <Alert severity="warning">
              {runs.length} run{runs.length === 1 ? '' : 's'} reference <strong>{pipeline.display_name}</strong>.
              Delete is blocked to keep run provenance intact.
            </Alert>
          ) : (
            <Typography variant="body2">
              Permanently delete <strong>{pipeline.display_name}</strong> (<code>{pipeline.name}</code>) and its
              version history? This cannot be undone.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>Cancel</Button>
        <Button onClick={doDelete} color="error" variant="contained" disabled={deleting || blocked}>
          {deleting ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PipelineDetail;
